<script lang="ts">
	import '../../app.css'
	import { normalizedClient } from '$lib/graphql/makeNormalizedClient'
	import { setContextClient } from '@urql/svelte'
	import { setContext } from 'svelte'
	import { createMutateTripContext } from '$lib/context/async-operations'
	import GoogleMaps from '$lib/components/maps/GoogleMaps.svelte'
	import { onMount } from 'svelte'
	import Modals from '$lib/components/modals/modals.svelte'
	import Sidebar from '$src/lib/components/navigation/Sidebar.svelte'
	import SignInForm from '$src/lib/components/auth/SignInForm.svelte'
	import { page } from '$app/state'
	import authState from '$lib/stores/auth-state'
	import Spinner from '$src/lib/components/loading/Spinner.svelte'
	import SvelteSeo from 'svelte-seo'
	import YoloAuth from '$src/lib/components/auth/YoloAuth.svelte'
	import GoogleTagManager from '$lib/components/analytics/GoogleTagManager.svelte'
	import FullStory from '$lib/components/analytics/FullStory.svelte'
	import TripMobileMenu from '$lib/components/navigation/TripMobileMenu.svelte'
	import { isMobile } from '$lib/utils/isMobile'
	import PanelLoading from '$lib/components/itineraries/PanelLoading.svelte'
	import ToastContainer from '$lib/components/toast/ToastContainer.svelte'
	import { urlState } from '$lib/stores/url-state'
	import {
		HandleCloseBlockPage,
		removePanelState,
		setFullScreenParam,
	} from '$lib/utils/mapRoutingState'
	import { browser } from '$app/environment'
	import blockState from '$lib/stores/block-state'
	import RoundIconButton from '$lib/components/buttons/RoundIconButton.svelte'
	import ExIcon from '$lib/components/icons/ExIcon.svelte'
	import UpIcon from '$lib/components/icons/UpIcon.svelte'
	import DownIcon from '$lib/components/icons/DownIcon.svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'
	import RightChevronIcon from '$components/icons/RightChevronIcon.svelte'
	import LeftChevronIcon from '$components/icons/LeftChevronIcon.svelte'
	import { goto } from '$app/navigation'

	interface LayoutProps {
		children: import('svelte').Snippet
	}

	let { children }: LayoutProps = $props()

	// Page data & SEO
	let title = $derived(page?.data.title)
	let description = $derived(page?.data.description)
	let homeType = $derived(page?.data?.homeType)

	let images = $derived([
		{
			url: 'https://airheart.com/airheart-social.png',
			width: 1200,
			height: 600,
			alt: title,
		},
	])

	setContextClient(normalizedClient)
	setContext('mutateTrip', createMutateTripContext(normalizedClient))

	let {
		panel: panelState,
		parentBlockId,
		itineraryId,
		blockId,
		isHighlights,
		isPublic,
		activeBlockId,
		tripId,
		requiresAuthentication,
		isFullScreen,
		isListing,
	} = $derived($urlState)

	// Auth
	let signedIn = $derived($authState.signedIn)
	let authDidInitialize = $derived($authState.authDidInitialize)

	// Panel/drag state
	let panelYOffset = $state(0) // 0% => mostly visible; 70% => mostly hidden
	let sensitivityMultiplier = $state(1.5)
	let startY: number
	let panelRef: HTMLElement = $state(null)
	let draggingPanel = $state(false)
	let dragStartTime = $state(0)
	let dragDistance = $state(0)

	$effect(() => {
		let cancelled = false

		if (isHighlights && !cancelled) {
			panelYOffset = 0
			if (browser) {
				setFullScreenParam(false)
			}
		}

		return () => {
			cancelled = true
		}
	})

	// For dynamic map height
	let mapHeightOffset = $derived(panelYOffset === 100 ? 100 : 31)
	let panelStyle = $derived(`--translate-y-percentage: ${panelYOffset}%;`)
	let mapStyle = $derived(`height: ${mapHeightOffset}dvh;`)

	// Mobile vs. desktop
	let isMobileScreen = $state(isMobile())

	function checkIsMobile() {
		// Force reactivity update
		isMobileScreen = isMobile()
	}

	onMount(() => {
		checkIsMobile()

		if (isMobileScreen) {
			window.addEventListener('touchmove', duringDrag, { passive: true })
			window.addEventListener('touchend', endDrag, { passive: true })
			window.addEventListener('mousemove', duringDrag)
			window.addEventListener('mouseup', endDrag)
		}
		window.addEventListener('resize', checkIsMobile)

		return () => {
			if (isMobileScreen) {
				window.removeEventListener('touchmove', duringDrag)
				window.removeEventListener('touchend', endDrag)
				window.removeEventListener('mousemove', duringDrag)
				window.removeEventListener('mouseup', endDrag)
			}
			window.removeEventListener('resize', checkIsMobile)
		}
	})

	function startDrag(event: MouseEvent | TouchEvent) {
		if (isHighlights) return

		const touchEvent = (event as TouchEvent).touches
			? (event as TouchEvent).touches[0]
			: (event as MouseEvent)

		const panelTop = panelRef.getBoundingClientRect().top
		const dragThreshold = 60 // Changed from 90 to 60px for top drag area

		// Record initial touch/click position and time
		if (touchEvent.clientY - panelTop <= dragThreshold) {
			startY = touchEvent.clientY
			dragStartTime = Date.now()
			dragDistance = 0
			draggingPanel = true
		} else {
			draggingPanel = false
		}
	}

	function duringDrag(event: MouseEvent | TouchEvent) {
		if (!draggingPanel) return

		const touchEvent = (event as TouchEvent).touches
			? (event as TouchEvent).touches[0]
			: (event as MouseEvent)

		// Calculate drag distance
		dragDistance = Math.abs(touchEvent.clientY - startY)

		let deltaY = (touchEvent.clientY - startY) * sensitivityMultiplier

		// If we're in fullscreen, only allow dragging down
		if (isFullScreen) {
			if (deltaY < 0) return // Prevent dragging up when in fullscreen
			deltaY = Math.max(0, deltaY) // Only allow positive (downward) movement
		}

		// Calculate new offset based on current state
		let newOffset: number
		if (isFullScreen) {
			// When in fullscreen, start from 0 and only go down
			newOffset = (deltaY / window.innerHeight) * 100
		} else {
			// Normal panel movement
			newOffset = panelYOffset + (deltaY / window.innerHeight) * 100
		}

		// Constrain the offset between 0 and 100
		panelYOffset = Math.max(0, Math.min(100, newOffset))
	}

	function endDrag() {
		if (!draggingPanel) return

		const dragDuration = Date.now() - dragStartTime

		// If it was a short duration and minimal movement, it was likely a click, not a drag
		if (dragDuration < 200 && dragDistance < 10) {
			// This was a click, not a drag
			draggingPanel = false
			return
		}

		const snapThresholdUp = 30 // Threshold for snapping upward
		const snapThresholdDown = 60 // Threshold for snapping downward

		if (isFullScreen) {
			// If dragging from fullscreen state
			if (panelYOffset > snapThresholdDown) {
				// Dragged far enough down to minimize
				panelYOffset = 100
				if (browser) {
					setFullScreenParam(false)
				}
			} else if (panelYOffset > snapThresholdUp) {
				// Dragged to normal position
				panelYOffset = 0
				if (browser) {
					setFullScreenParam(false)
				}
			} else {
				// Return to fullscreen
				panelYOffset = 0
				if (browser) {
					setFullScreenParam(true)
				}
			}
		} else {
			// If dragging from normal/minimized state
			if (panelYOffset < snapThresholdUp) {
				// Dragged up to fullscreen
				panelYOffset = 0
				if (browser) {
					setFullScreenParam(true)
				}
			} else if (panelYOffset < snapThresholdDown) {
				// Dragged to normal position
				panelYOffset = 0
				if (browser) {
					setFullScreenParam(false)
				}
			} else {
				// Dragged to minimized position
				panelYOffset = 100
				if (browser) {
					setFullScreenParam(false)
				}
			}
		}

		draggingPanel = false
	}

	let isMinimizedView = $derived(panelYOffset === 100)

	function toggleDesktopFullScreen() {
		if (browser) {
			setFullScreenParam(!isFullScreen)
		}
		if (isHighlights || blockId != null) {
			blockState.setMapState('block')
		} else {
			blockState.setMapState('trip')
		}
		blockState.setRecenterMap(true)
	}

	// Up/Down buttons
	function moveUp() {
		if (isFullScreen || isHighlights) return
		if (panelYOffset === 0) {
			if (browser) {
				setFullScreenParam(true)
			}

			blockState.setRecenterMap(true) // Force map update
		} else if (panelYOffset === 100) {
			panelYOffset = 0
			if (browser) {
				setFullScreenParam(false)
			}
		}
	}

	function moveDown() {
		if (isHighlights) return
		if (isFullScreen) {
			panelYOffset = 0
			if (browser) {
				setFullScreenParam(false)
			}
			blockState.setRecenterMap(true) // Force map update
		} else if (panelYOffset === 0) {
			panelYOffset = 100
			if (browser) {
				setFullScreenParam(false)
			}
		}
	}

	function closePanel() {
		if (panelState != null) {
			blockState?.clearAllCustom()
			removePanelState(page.url.pathname, browser, itineraryId, activeBlockId)
		} else if (isListing) {
			goto(`/trips/${tripId}`, { keepFocus: false })
		} else {
			HandleCloseBlockPage(tripId, parentBlockId, browser, isPublic, itineraryId)
		}
		if ($blockState.mapState === 'search') {
			if (isHighlights || blockId != null) {
				blockState.setMapState('block')
			} else {
				blockState.setMapState('trip')
			}
		}
		blockState.setRecenterMap(true)
	}
</script>

<svelte:head>
	<link rel="shortcut icon" href="/favicon.ico" />
	<meta name="theme-color" content="#FF006E" />
	<title>{page?.data?.title ?? 'Airheart'}</title>
	<meta name="description" content={page?.data?.description ?? ''} />
	<script src="https://embed.cloudflarestream.com/embed/sdk.latest.js"></script>
</svelte:head>

<div
	class="dynamic-viewport flex w-full lg:-mb-2 overflow-clip"
	data-sveltekit-preload-data="hover"
>
	<SvelteSeo
		title={page?.data?.title ?? 'Airheart'}
		jsonLd={{
			'@type': 'Corporation',
			logo: 'https://airheart.com/airheart-logo-full-new.svg',
			url: 'https://airheart.com',
			name: 'Airheart, Inc.',
			sameAs: [
				'https://twitter.com/AirheartInc',
				'https://instagram.com/airheartinc',
				'https://www.linkedin.com/company/airheartinc',
				'https://www.facebook.com/AirheartInc',
			],
		}}
		twitter={{
			description,
			title,
			image: `https://airheart.com/marketing/marketing/airheart-social.png`,
			imageAlt: title,
			site: '@airheartinc',
			card: 'summary_large_image',
		}}
		openGraph={{
			title,
			images,
			description,
		}}
	/>

	<!-- Desktop View -->
	{#if !isMobileScreen}
		<Sidebar />
		<div class="hidden w-full lg:flex">
			<main
				class={`
					bg-white dark:bg-brand-gray-5
					text-black dark:text-white
					overflow-y-scroll max-h-screen
					transition-all duration-300 ease-in-out flex-shrink-0
					${isFullScreen ? 'w-full' : 'w-[480px]'}
				`}
			>
				{#if requiresAuthentication && !authDidInitialize}
					<div
						class="flex items-center justify-center min-h-screen text-center text-black dark:text-white w-full flex-shrink-0"
					>
						<Spinner size="md" />
					</div>
				{:else if requiresAuthentication && !signedIn}
					<div
						class={`${isFullScreen ? 'w-full' : 'w-[480px]'} px-4 m-auto h-screen transition-all duration-300 ease-in-out`}
					>
						<SignInForm>
							<h3
								class="py-2 mb-4 text-2xl font-medium leading-8 tracking-tight dark:text-white text-black sm:mt-4"
							>
								Sign in to Airheart
							</h3>
						</SignInForm>
					</div>
				{:else}
					<div
						class={`
						transition-all duration-300 ease-in-out
						${isFullScreen ? 'max-w-[800px] mx-auto' : 'max-w-[480px]'}
						w-full
					`}
					>
						{@render children?.()}
					</div>
				{/if}

				<!-- Desktop control buttons -->
				<div
					class={`
							pointer-events-auto
							absolute
							top-5
							z-30
							flex
							flex-col
							gap-2
							${isFullScreen ? 'right-4' : 'left-[560px]'}
						`}
				>
					{#if !isFullScreen}
						<RoundIconButton title="Toggle Fullscreen" onclick={toggleDesktopFullScreen}>
							<RightChevronIcon class="h-4 w-4 m-auto text-black dark:text-white" />
						</RoundIconButton>

						{#if panelState != null || blockId != null || isHighlights || isListing}
							<RoundIconButton title="Close Panel" onclick={closePanel}>
								<ExIcon class="h-4 w-4 m-auto text-black dark:text-white" />
							</RoundIconButton>
						{/if}
					{:else}
						<RoundIconButton
							title="Toggle Fullscreen"
							onclick={toggleDesktopFullScreen}
							class="text-black bg-white hover:dark:bg-brand-gray-2"
						>
							<LeftChevronIcon class="h-4 w-4 m-auto text-black dark:text-black" />
						</RoundIconButton>

						{#if panelState != null || blockId != null || isHighlights || isListing}
							<RoundIconButton
								title="Close Panel"
								onclick={closePanel}
								class="text-black bg-white hover:dark:bg-brand-gray-2"
							>
								<ExIcon class="h-4 w-4 m-auto text-black dark:text-black" />
							</RoundIconButton>
						{/if}
					{/if}
				</div>
			</main>
			<aside
				class={`
					h-full w-full top-0 sticky overflow-hidden
					transition-all duration-300 ease-in-out
					${isFullScreen ? 'w-0 opacity-0' : 'w-full opacity-100'}
				`}
			>
				<GoogleMaps />
			</aside>
			<ToastContainer />
		</div>
	{:else}
		<!-- Mobile View -->
		<main class="block lg:hidden relative dynamic-viewport w-full overflow-clip">
			{#if !isFullScreen}
				<TripMobileMenu />
			{/if}

			{#if page.data.requiresAuthentication && !authDidInitialize}
				<PanelLoading />
			{:else if requiresAuthentication && !signedIn}
				<div class="w-full px-4 m-auto">
					<SignInForm>
						<h3
							class="py-2 my-16 text-2xl font-medium leading-8 tracking-tight dark:text-white text-black sm:mt-8"
						>
							Sign in to Create or View Your Trips
						</h3>
					</SignInForm>
				</div>
			{:else}
				<!-- MAP in the background -->
				<div
					class="absolute inset-0 h-full w-full overflow-hidden block"
					style={!isHighlights ? mapStyle : ''}
				>
					<ToastContainer isMobileScreen={true} />
					<GoogleMaps />
				</div>

				<!--
				  A container sharing the same translateY for the panel and the buttons,
				  so they move together.
				-->
				<div
					class="
						absolute
						inset-x-0
						bottom-0
						transition-transform
						dynamic-translate-y
						pointer-events-none
						z-30
					"
					style={!isHighlights ? panelStyle : '--translate-y-percentage: 0%;'}
				>
					<!-- Draggable panel -->
					<div
						bind:this={panelRef}
						onmousedown={startDrag}
						ontouchstart={startDrag}
						role="button"
						tabindex="0"
						class={`
							pointer-events-auto
							relative
							w-full
							dark:text-white
						text-black
						scroll-container
						overscroll-contain
							${
								isFullScreen
									? 'max-h-[100dvh] bg-white  dark:bg-brand-gray-5 pt-8'
									: 'max-h-[70dvh] rounded-t-xl'
							}
							${!isHighlights ? 'shadow-top overflow-y-auto dark:bg-brand-gray-5' : 'mb-2'}
							`}
					>
						{#if requiresAuthentication && !authDidInitialize}
							<PanelLoading />
						{:else}
							{#if isFullScreen}
								<div
									class="
								pointer-events-auto
								fixed
								left-0
								top-0
								flex
								flex-row
								items-center
								z-30
								w-full
								h-11
								px-4
								pt-2
								bg-brand-gray-6
							"
								>
									<!-- UP button -->
									<button
										disabled={isFullScreen}
										onclick={stopPropagation(moveUp)}
										class="
											bg-brand-gray-6
											text-white
											rounded-full
											flex items-center justify-center
											shadow-md
											active:opacity-80
											disabled:opacity-50
											h-9 w-9
											touch-manipulation
										"
										title="Move panel up"
									>
										<UpIcon class="h-4 w-4 m-auto text-black dark:text-white" />
										<!-- <Icon src={ChevronUp} class="h-4 w-4 m-auto text-black dark:text-white" /> -->
									</button>

									<!-- DOWN button -->
									<button
										disabled={!isFullScreen}
										onclick={stopPropagation(moveDown)}
										class="
											bg-brand-gray-6
											text-white
											rounded-full
											flex items-center justify-center
											shadow-md
											active:opacity-80
											disabled:opacity-50
											h-9 w-9
											touch-manipulation
										"
										title="Move panel down"
									>
										<DownIcon class="h-4 w-4 m-auto text-black dark:text-white" />
										<!-- <Icon src={ChevronDown} class="h-4 w-4 m-auto text-black dark:text-white" /> -->
									</button>
								</div>

								{#if panelState != null || blockId != null}
									<div
										class="
											pointer-events-auto
											fixed
											right-0
											top-0
											flex
											flex-row
											items-center
											justify-end
											z-30
											h-11
											px-4
											pt-2
											bg-brand-gray-6
										"
									>
										<button
											onclick={stopPropagation(closePanel)}
											class="
												bg-brand-gray-6
												text-white
													rounded-full
													flex items-center justify-center
													shadow-md
													active:opacity-80
													disabled:opacity-50
													h-9 w-9
													touch-manipulation
											"
											title="Move panel down"
										>
											<ExIcon class="h-4 w-4 m-auto text-black dark:text-white" />
										</button>
									</div>
								{/if}
							{/if}

							{#if !isMinimizedView}
								{@render children?.()}
							{/if}
						{/if}
					</div>

					<!--
					  The Up/Down buttons in a row, 4rem above the panel's top edge (right side).
					  Background black + white icons, perfectly round.
					-->
					{#if isMobileScreen && !isHighlights}
						<div
							class={`pointer-events-auto
								absolute
								left-2
								flex
									flex-row
									items-center
									space-x-2
									z-30
									${isMinimizedView && blockId != null ? '-top-32' : '-top-12'}
								`}
						>
							<!-- UP button -->
							<RoundIconButton title="Move panel up" onclick={moveUp} disabled={isFullScreen}>
								<UpIcon class="h-4 w-4 m-auto text-black dark:text-white" />
							</RoundIconButton>

							<!-- DOWN button -->

							<RoundIconButton
								title="Move panel down"
								onclick={moveDown}
								disabled={panelYOffset === 100}
							>
								<DownIcon class="h-4 w-4 m-auto text-black dark:text-white" />
							</RoundIconButton>
						</div>
					{/if}

					{#if panelState != null || blockId != null}
						<div
							class={`pointer-events-auto
						absolute
						right-2
						flex
							flex-row
							items-center
							space-x-2
							z-30
							${isMinimizedView && blockId != null ? '-top-32' : '-top-12'}
						`}
						>
							<!-- DOWN button -->
							<RoundIconButton title="Close Panel" onclick={closePanel}>
								<ExIcon class="h-4 w-4 m-auto text-black dark:text-white" />
							</RoundIconButton>
						</div>
					{/if}

					{#if isMinimizedView && blockId != null}
						<div class="absolute w-full left-0 -top-20">
							{@render children?.()}
						</div>
					{/if}
				</div>
			{/if}
		</main>
	{/if}

	<style>
		.dynamic-viewport {
			height: 100dvh; /* Helps fill the screen on mobile */
		}

		/* Add this to your existing dynamic-translate-y class */
		.dynamic-translate-y {
			transform: translateY(var(--translate-y-percentage, 0%));
			transform-origin: bottom;
			will-change: transform;
		}
		.shadow-top {
			box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
		}
	</style>
</div>

<Modals />
<YoloAuth isMapPage={true} />
<GoogleTagManager />
<FullStory />
